import { EMPTY_ARRAY } from "@/lib/utils/stable-empty";
import React, { useState, useMemo, useCallback } from "react";
import { useActionFiltering } from "@/lib/hooks/search/useActionFiltering";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CheckIcon, X, Edit3, ChevronDown } from "lucide-react";
import { BulkEditModal } from "./BulkEditModal";
import { SelectAllPagesModal } from "./SelectAllPagesModal";
import type { SelectAllPagesResult } from "./services/selectAllPagesService";
import { generateBulkActionsFromRowActions } from "./utils/generateBulkActionsFromRowActions";
import { useBulkActionGroups } from "./utils/bulkActionGroups";
import type { BaseEntity, FieldValue } from "../UniversalCRMTable/types";
import type { GroupedBulkActionsProps, BulkActionWithOnClick } from "./types";

// Re-export types for backward compatibility
export type { BaseEntity, BulkActionGroup, BulkAction, BulkActionWithOnClick } from "./types";

export function GroupedBulkActions<T extends BaseEntity>({
  selectedItems,
  allItems,
  config,
  onClearSelection,
  onSelectAll,
  onBulkEdit,
  onBulkEditAllFiltered,
  appUser,
  totalFilteredCount,
  filterCriteria,
  customActions = EMPTY_ARRAY,
  className = "",
  onSelectAllPages,
  fetchAllPages,
  fetchAllPagesGraphQL,
  pageSize = 100,
  maxPages = 1000,
  rowActions,
  actionContext,
  editAllFieldsModal: EditAllFieldsModalComponent,
}: GroupedBulkActionsProps<T>) {
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [bulkEditField, setBulkEditField] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showSelectAllPagesModal, setShowSelectAllPagesModal] = useState(false);
  const [showEditAllFieldsModal, setShowEditAllFieldsModal] = useState(false);

  const { filterActionsByRole } = useActionFiltering();

  const generatedBulkActions = useMemo(() => {
    if (!rowActions || !actionContext) return [];
    const roleFilteredActions = filterActionsByRole(rowActions);
    return generateBulkActionsFromRowActions(roleFilteredActions, actionContext, selectedItems);
  }, [rowActions, actionContext, selectedItems, filterActionsByRole]);

  const allCustomActions = useMemo(
    () => [...generatedBulkActions, ...customActions],
    [generatedBulkActions, customActions]
  );

  const handleSelectAllPages = useCallback(() => {
    if (onSelectAllPages) {
      onSelectAllPages();
    } else if (fetchAllPages || fetchAllPagesGraphQL) {
      setShowSelectAllPagesModal(true);
    }
  }, [onSelectAllPages, fetchAllPages, fetchAllPagesGraphQL]);

  const bulkEditableFields = useMemo(
    () =>
      Object.entries(config.fields)
        .filter(([_, fieldConfig]) => fieldConfig.bulkEditable && !fieldConfig.formOnly)
        .map(([fieldId, fieldConfig]) => ({ fieldId, ...fieldConfig })),
    [config.fields]
  );

  const actionGroups = useBulkActionGroups({
    bulkEditableFields,
    totalFilteredCount,
    allItemsLength: allItems.length,
    allCustomActions: allCustomActions as BulkActionWithOnClick<T>[],
    onSetBulkEditField: setBulkEditField,
    onSetShowBulkEditModal: setShowBulkEditModal,
    onSelectAllPages: handleSelectAllPages,
  });

  const handleBulkEdit = async (fieldId: string, value: FieldValue, itemIds: string[]) => {
    setIsLoading(true);
    try {
      await onBulkEdit(fieldId, value, itemIds);
    } catch (error) {
      console.error("Bulk edit error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkEditAllFiltered = async (
    fieldId: string,
    value: FieldValue,
    filterCriteria?: Record<string, unknown>
  ) => {
    if (!onBulkEditAllFiltered) return;
    setIsLoading(true);
    try {
      await onBulkEditAllFiltered(fieldId, value, filterCriteria);
    } catch (error) {
      console.error("Bulk edit all filtered error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAllPagesComplete = (result: SelectAllPagesResult<T>) => {
    console.log("Select All Pages completed:", result);
    setShowSelectAllPagesModal(false);
  };

  if (selectedItems.length === 0) return null;

  return (
    <>
      <div
        className={`fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 bg-card border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg px-4 py-3 w-[min(400px,90vw)] ${className}`}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Badge variant="secondary" className="bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">
              {selectedItems.length} selected
            </Badge>
            <Button variant="outline" size="sm" onClick={onClearSelection} className="gap-2">
              <X className="h-4 w-4" />
              Clear
            </Button>
            {onSelectAll && (
              <Button variant="outline" size="sm" onClick={onSelectAll} className="gap-2">
                <CheckIcon className="h-4 w-4" />
                Select All
              </Button>
            )}
          </div>

          <div className="flex items-end gap-3 flex-wrap justify-end">
            {EditAllFieldsModalComponent && (
              <Button
                variant="outline"
                size="sm"
                className="flex flex-col items-center justify-center h-auto py-2 px-2 gap-1"
                onClick={() => setShowEditAllFieldsModal(true)}
              >
                <div className="flex items-center justify-center">
                  <Edit3 className="h-4 w-4" />
                </div>
                <span className="text-xs font-medium">Edit Selected</span>
              </Button>
            )}
            {actionGroups
              .filter((group) => group.actions.length > 0)
              .map((group) => (
                <DropdownMenu key={group.id}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex flex-col items-center justify-center h-auto py-2 px-2 gap-1"
                    >
                      <div className="flex items-center justify-center">{group.icon}</div>
                      <div className="flex items-center justify-center gap-1">
                        <span className="text-xs font-medium">{group.label}</span>
                        <ChevronDown className="h-3 w-3" />
                      </div>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>{group.label}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {group.actions.map((action) => {
                      const actionWithCallback = action as BulkActionWithOnClick<T>;
                      return (
                        <DropdownMenuItem
                          key={action.id}
                          onClick={async () => {
                            if (actionWithCallback.onClick) {
                              await actionWithCallback.onClick(
                                selectedItems,
                                selectedItems.map((item) => item.id)
                              );
                            }
                          }}
                          disabled={
                            action.disabled?.(selectedItems) || action.hidden?.(selectedItems)
                          }
                          className={action.variant === "destructive" ? "text-red-600" : ""}
                        >
                          <div className="flex items-center gap-2">
                            <span className="shrink-0 w-4 h-4 flex items-center justify-center">
                              {React.isValidElement(action.icon) ? action.icon : null}
                            </span>
                            <span>{action.label}</span>
                          </div>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              ))}
          </div>
        </div>
      </div>

      {showBulkEditModal && bulkEditField && (
        <BulkEditModal
          isOpen={showBulkEditModal}
          onClose={() => setShowBulkEditModal(false)}
          fieldId={bulkEditField}
          fieldConfig={config.fields[bulkEditField]}
          selectedItems={selectedItems}
          allItems={allItems}
          config={config}
          onBulkEdit={handleBulkEdit}
          onBulkEditAllFiltered={handleBulkEditAllFiltered}
          appUser={appUser}
          totalFilteredCount={totalFilteredCount}
          filterCriteria={filterCriteria}
        />
      )}

      {EditAllFieldsModalComponent && showEditAllFieldsModal && (
        <EditAllFieldsModalComponent
          selectedItems={selectedItems}
          isOpen={showEditAllFieldsModal}
          onClose={() => setShowEditAllFieldsModal(false)}
          clearSelection={onClearSelection}
        />
      )}

      {showSelectAllPagesModal && (fetchAllPages || fetchAllPagesGraphQL) && (
        <SelectAllPagesModal
          isOpen={showSelectAllPagesModal}
          onClose={() => setShowSelectAllPagesModal(false)}
          onComplete={handleSelectAllPagesComplete}
          fetchPage={fetchAllPages!}
          fetchPageGraphQL={fetchAllPagesGraphQL}
          pageSize={pageSize}
          maxPages={maxPages}
          title="Select All Pages"
          description="Fetching all data across all pages for bulk actions..."
        />
      )}
    </>
  );
}

// Re-export action group creators for backward compatibility
export { createContactBulkActionGroups, createTaskBulkActionGroups } from "./action-group-creators";
